import React from 'react';
import { FlexGroup, FlexItem, Icon, IconType, Link } from '.';
import { IconSize } from './Icon';
import Badge, { BadgeProps, BadgeSize } from './Badge';
import { LithosComponent } from './LithosComponent';
import styled from '@emotion/styled';

type BreadcrumbSize = 's' | 'm';

interface BreadcrumbRoot {
  label: string;
  url: string;
  icon?: IconType;
}

export interface BreadcrumbProps {
  root: BreadcrumbRoot;
  items: BreadcrumbItem[];
  size?: BreadcrumbSize;
}

interface BreadcrumbItem {
  label: React.ReactNode;
  url?: string;
  badge?: BadgeProps;
}

const IconSizeMap: Record<BreadcrumbSize, IconSize> = {
  s: 's',
  m: 'm',
};

const BadgeSizeMap: Record<BreadcrumbSize, BadgeSize> = {
  s: 'xs',
  m: 's',
};

const TruncateContents = styled.div`
  * {
    min-width: 5em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Chevron: LithosComponent<{ size: BreadcrumbSize }> = ({ size }) => {
  return (
    <div style={{ flexShrink: '0', flexGrow: '0' }}>
      <FlexGroup alignItems="center" padding="0">
        <Icon icon="ChevronRight" size={IconSizeMap[size]} type="mono" />
      </FlexGroup>
    </div>
  );
};

const IntermediateBreadcrumb: LithosComponent<{ item: BreadcrumbItem; size: BreadcrumbSize }> = ({ item, size }) => {
  return (
    <>
      <FlexItem grow="0" shrink="1" overflow="hidden">
        <FlexGroup padding="0" gapSize="1" alignItems="center">
          <TruncateContents>
            <Link name={item.label} url={item.url} size={size} testId={`crumb-current-${item.label}`} />
          </TruncateContents>

          {item.badge && <Badge {...item.badge} size={BadgeSizeMap[size]} color="pink" />}
        </FlexGroup>
      </FlexItem>
      <Chevron size={size} />
    </>
  );
};

const Breadcrumbs: LithosComponent<BreadcrumbProps> = ({ root, items, size = 'm', testId = 'breadcrumbs' }) => {
  return (
    <FlexGroup padding="0" gapSize={size === 'm' ? '1' : '0.5'} alignItems="center" data-testid={testId}>
      <FlexItem grow="0" shrink="0">
        <Link name={root.label} icon={root.icon} url={root.url} size={size} iconType="mono" testId="crumb-root" />
      </FlexItem>
      {items.length > 0 && <Chevron size={size} />}
      {items.map((item, index) => {
        // Final breadcrumb
        if (index === items.length - 1) {
          return (
            <FlexItem grow="0" overflow="hidden">
              <FlexGroup padding="0" alignItems="center" gapSize="1.5">
                <TruncateContents>
                  <Link name={item.label} size={size} testId="crumb-current" />
                </TruncateContents>
                {item.badge && <Badge {...item.badge} size={BadgeSizeMap[size]} color="pink" />}
              </FlexGroup>
            </FlexItem>
          );
        }
        // Intermediate breadcrumb
        return <IntermediateBreadcrumb item={item} size={size} />;
      })}
    </FlexGroup>
  );
};

export default Breadcrumbs;
